import { ModerationRequest, ModerationStatus, ModerationRequestType } from "../../../models";
import { IPlace } from "../../models/Models";
import { DataStoreTable } from "./DataStoreTable";
import { applyChange, diff, Diff } from "deep-diff";
import { Kernel } from "../../Kernel";
import cloneDeep from "lodash-es/cloneDeep.js";
import fluent, { FluentIterable } from "fluent-iterable";
import { DataProviderArgs } from "../../DataStore/DataProvider";

export enum DiffKind {
    New = "N",
    Deleted = "D",
    Changed = "E",
    ArrayChanged = "A",
}

export type IModerationRequest = {
    id: string;

    /** Whether the contributor wants to remain anonymous */
    anonymous: boolean;

    /** Actual request contents. Depends on ModerationRequestType */
    payload: string;

    type: ModerationRequestType;
    status: ModerationStatus;
    groupAccess: string[];
    owner: string;
    createdAt: string;
};

export type IPlaceModerationPayload = {
    id: string;
    version: number;
    diff: Diff<IPlace>[];
};

function createPlaceModerationRequest(obj1: IPlace, obj2: IPlace, anonymous: boolean) {
    return {
        id: obj1.id,
        anonymous,
        payload: JSON.stringify({
            id: obj1.id,
            diff: diff(obj1, obj2),
            version: 1,
        }),
        type: ModerationRequestType.PLACE,
        status: ModerationStatus.SUBMITTED,

        // Filter out readonly group so only editors have permission to this object
        groupAccess: obj1.groupAccess?.filter((x) => !x.includes("Readonly")),
    } as IModerationRequest;
}

/**
 * Moderation request table. This will contain both moderation requests a user has made
 * as well as requests that need to be moderated. The primary use is for place updates but
 * it may need to support other models as well (i.e. GroundWater).
 *
 * For places diffs are stored in a IPlaceModerationPayload object using deep-diff
 * (https://github.com/flitbit/diff).
 */
export class ModerationTable extends DataStoreTable<IModerationRequest> {
    private _kernel = Kernel.instance();

    constructor(args: DataProviderArgs) {
        super(
            args,
            "moderation",
            ModerationRequest,
            (obj: any) => obj as IModerationRequest,
            (obj: any) => new ModerationRequest(obj)
        );
    }

    public getOutstandingRequests(): FluentIterable<IModerationRequest> {
        return this.allSync().filter(
            (x: IModerationRequest) => x.status === ModerationStatus.SUBMITTED
        );
    }

    public getUserRequests(): FluentIterable<IModerationRequest> {
        const user = Kernel.instance().user;
        if (user) {
            return this.allOwnedBySync(user.id).filter(
                (x) =>
                    x.status !== ModerationStatus.REJECTED && x.status !== ModerationStatus.APPROVED
            );
        }
        return fluent([]);
    }

    public submitPlaceModerationRequest(place: IPlace, modified: IPlace, anonymous: boolean) {
        const request = createPlaceModerationRequest(place, modified, anonymous);
        console.log(request);
        this.create(request);
    }

    public async acceptPlaceRequest(
        request: IModerationRequest,
        placeId: string,
        diffs: Diff<IPlace>[]
    ) {
        const places = this._kernel.dataStore.getSource("places");
        if (places) {
            const place = cloneDeep(await places.read(placeId));
            if (place) {
                for (const diff of diffs) {
                    applyChange(place, undefined, diff);
                }
                await places.update(place, true, request.anonymous ? undefined : request.owner);
                await this.delete(request.id);
            }
        }
    }

    public async rejectRequest(request: IModerationRequest) {
        await this.delete(request.id);
    }
}
